import { api } from "@/lib/apiClient";
import type { ID } from "@/lib/catalog/types";

/** Seeded defaults used as fallback when API has not synced yet. */
export const DOCUMENT_TYPE_SEED_OPTIONS = [
  "Invoice",
  "Registration Book",
  "Smart Card",
  "Token Tax",
  "Insurance",
  "Transfer Letter",
  "Sale Receipt",
  "Purchase Receipt",
  "CNIC Copy",
  "NOC",
  "Vehicle Images",
  "Inspection Report",
  "Agreement",
  "Other",
] as const;

export interface DocumentTypeRecord {
  id: ID;
  name: string;
  createdAt?: string;
}

const LS_KEY = "inventory.documentTypes.v1";

type DocumentTypeApiRecord = {
  id: string;
  name: string;
  created_at?: string;
};

type ListDocumentTypesApiResponse = {
  data?: DocumentTypeApiRecord[];
};

type UpsertDocumentTypeApiResponse = {
  message?: string;
  data?: DocumentTypeApiRecord;
};

type UpsertDocumentTypeResult = {
  record: DocumentTypeRecord;
  message: string;
};

function dedupeById(items: DocumentTypeRecord[]): DocumentTypeRecord[] {
  const byId = new Map<ID, DocumentTypeRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function mapApiRecord(record: DocumentTypeApiRecord): DocumentTypeRecord {
  return {
    id: record.id,
    name: record.name,
    createdAt: record.created_at,
  };
}

function load(): DocumentTypeRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DocumentTypeRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: DocumentTypeRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listDocumentTypeRecords(): DocumentTypeRecord[] {
  const items = load();
  if (items.length > 0) {
    return items.sort((a, b) => a.name.localeCompare(b.name));
  }
  return DOCUMENT_TYPE_SEED_OPTIONS.map((name, index) => ({
    id: `seed-${index}`,
    name,
  }));
}

export function listDocumentTypeNames(): string[] {
  return listDocumentTypeRecords().map((t) => t.name);
}

export async function syncDocumentTypesFromApi() {
  const response = await api.get<ListDocumentTypesApiResponse>("/api/v1/catalog/document-types", {
    params: {
      page: 1,
      per_page: 200,
    },
  });
  const records = (response.data?.data ?? []).map(mapApiRecord);
  save(records);
  return listDocumentTypeRecords();
}

export async function createDocumentType(data: { name: string }): Promise<UpsertDocumentTypeResult> {
  const items = load();
  const payload = { name: data.name.trim() };
  const response = await api.post<UpsertDocumentTypeApiResponse>("/api/v1/catalog/document-types", payload);
  const created = response.data?.data;
  if (!created) {
    throw new Error("Invalid create document type response.");
  }
  const record = mapApiRecord(created);
  const existingIndex = items.findIndex((item) => item.id === record.id);
  if (existingIndex >= 0) {
    items[existingIndex] = record;
  } else {
    items.push(record);
  }
  save(items);
  return {
    record,
    message: response.data?.message || "Document type created successfully.",
  };
}
